import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import api from "api/axios";

const CATEGORIES = [
  { val: "hotel", label: "Hôtel" },
  { val: "transport", label: "Transport" },
  { val: "visa", label: "Visa" },
  { val: "restauration", label: "Restauration" },
  { val: "encadrement", label: "Encadrement" },
  { val: "divers", label: "Divers" },
];
const catLabel = (v) => (CATEGORIES.find((c) => c.val === v) || { label: v }).label;
const fmt = (n) => (Number(n) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 2 });

function OumrahBilan() {
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [couts, setCouts] = useState([]);
  const [bilan, setBilan] = useState(null);
  const [form, setForm] = useState({ libelle: "Hôtel", montant: "", type: "par_personne", categorie: "hotel" });
  const [archiveesUniquement, setArchiveesUniquement] = useState(false);

  useEffect(() => {
    api.get("/oumrah/sessions?archivees=true").then((r) => setSessions(r.data)).catch(() => {});
  }, []);

  const charger = () => {
    if (!sessionId) { setCouts([]); setBilan(null); return; }
    api.get("/oumrah/couts/session/" + sessionId).then((r) => setCouts(r.data)).catch(() => {});
    api.get("/oumrah/couts/session/" + sessionId + "/bilan").then((r) => setBilan(r.data)).catch(() => {});
  };
  useEffect(charger, [sessionId]);

  const sessionsAffichees = sessions.filter((s) => archiveesUniquement ? s.statut === "archivee" : s.statut !== "archivee");
  const sessionActuelle = sessions.find((s) => s.id === sessionId);
  const estArchivee = sessionActuelle && sessionActuelle.statut === "archivee";

  const cloturer = async () => {
    if (!window.confirm("Clôturer et archiver cette session ? Elle n'acceptera plus de nouvelles inscriptions. Le bilan restera consultable.")) return;
    try {
      await api.put("/oumrah/sessions/" + sessionId + "/archiver");
      const r = await api.get("/oumrah/sessions?archivees=true");
      setSessions(r.data);
      alert("Session clôturée et archivée.");
    } catch (err) { alert("Erreur lors de la clôture"); }
  };

  const ajouter = async () => {
    if (!form.libelle.trim()) return;
    try {
      await api.post("/oumrah/couts/session/" + sessionId, {
        libelle: form.libelle.trim(),
        montant: parseFloat(form.montant) || 0,
        type: form.type,
        categorie: form.categorie,
      });
      setForm({ libelle: "Hôtel", montant: "", type: "par_personne", categorie: "hotel" });
      charger();
    } catch (err) { alert("Erreur"); }
  };

  const supprimer = async (id) => {
    if (!window.confirm("Supprimer ce frais ?")) return;
    try { await api.delete("/oumrah/couts/" + id); charger(); } catch (err) { alert("Erreur"); }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ p: 3 }}>
              <MDTypography variant="h5" mb={1}>Bilan financier d'une session</MDTypography>
              <MDTypography variant="button" color="text" mb={2} display="block">
                Saisissez les frais de la session et consultez le bilan (recettes des inscriptions payées − dépenses).
              </MDTypography>
              <FormControl size="small" sx={{ minWidth: 280 }}>
                <InputLabel id="sess-bilan">Session</InputLabel>
                <Select labelId="sess-bilan" label="Session" sx={{ height: 44 }}
                  value={sessionId} onChange={(e) => setSessionId(e.target.value)}>
                  <MenuItem value="">—</MenuItem>
                  {sessionsAffichees.map((s) => <MenuItem key={s.id} value={s.id}>{s.libelle}{s.statut === "archivee" ? " (archivée)" : ""}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControlLabel sx={{ ml: 1 }}
                control={<Checkbox checked={archiveesUniquement} onChange={(e) => { setArchiveesUniquement(e.target.checked); setSessionId(""); }} />}
                label="Archivées uniquement" />
              {sessionId && (estArchivee ? (
                <MDTypography variant="button" color="text" sx={{ ml: 2 }}>
                  <Icon sx={{ verticalAlign: "middle" }}>inventory_2</Icon> Session archivee
                </MDTypography>
              ) : (
                <MDButton color="dark" sx={{ ml: 2 }} onClick={cloturer}>
                  <Icon>inventory_2</Icon>&nbsp;Cloturer la session
                </MDButton>
              ))}
            </Card>
          </Grid>

          {sessionId && bilan && (
            <>
              {/* Synthèse */}
              <Grid item xs={12} md={4}>
                <Card sx={{ p: 3, borderTop: "4px solid #43a047" }}>
                  <MDTypography variant="button" color="text">Recettes (encaissées)</MDTypography>
                  <MDTypography variant="h3" sx={{ color: "#43a047" }}>{fmt(bilan.total_recettes)} €</MDTypography>
                  <MDTypography variant="caption" color="text">{bilan.places_vendues} place(s) payée(s)</MDTypography>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={{ p: 3, borderTop: "4px solid #e53935" }}>
                  <MDTypography variant="button" color="text">Dépenses</MDTypography>
                  <MDTypography variant="h3" sx={{ color: "#e53935" }}>{fmt(bilan.total_depenses)} €</MDTypography>
                  <MDTypography variant="caption" color="text">toutes catégories</MDTypography>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={{ p: 3, borderTop: "4px solid " + (bilan.resultat >= 0 ? "#1A73E8" : "#fb8c00") }}>
                  <MDTypography variant="button" color="text">Résultat</MDTypography>
                  <MDTypography variant="h3" sx={{ color: bilan.resultat >= 0 ? "#1A73E8" : "#fb8c00" }}>
                    {fmt(bilan.resultat)} €
                  </MDTypography>
                  <MDTypography variant="caption" color="text">{bilan.resultat >= 0 ? "Bénéfice" : "Perte"}</MDTypography>
                </Card>
              </Grid>

              {/* Tableau unique groupé par catégorie */}
              <Grid item xs={12}>
                <Card sx={{ p: 3 }}>
                  <MDTypography variant="h6" mb={2}>Frais de la session</MDTypography>
                  {/* Formulaire ajout */}
                  <MDBox display="flex" gap={1} flexWrap="wrap" mb={2} alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel id="cat-f">Catégorie</InputLabel>
                      <Select labelId="cat-f" label="Catégorie" sx={{ height: 40 }}
                        value={form.categorie} onChange={(e) => {
                          const nc = e.target.value;
                          const labels = CATEGORIES.map((x) => x.label);
                          const lib = (!form.libelle.trim() || labels.includes(form.libelle.trim())) ? catLabel(nc) : form.libelle;
                          setForm({ ...form, categorie: nc, libelle: lib });
                        }}>
                        {CATEGORIES.map((c) => <MenuItem key={c.val} value={c.val}>{c.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <TextField label="Libellé" size="small" value={form.libelle}
                      onChange={(e) => setForm({ ...form, libelle: e.target.value })} sx={{ flex: 1, minWidth: 150 }} />
                    <TextField label="Montant €" type="number" size="small" value={form.montant}
                      onChange={(e) => setForm({ ...form, montant: e.target.value })} sx={{ width: 110 }} />
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel id="type-f">Type</InputLabel>
                      <Select labelId="type-f" label="Type" sx={{ height: 40 }}
                        value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                        <MenuItem value="par_personne">Par personne</MenuItem>
                        <MenuItem value="fixe_groupe">Fixe (groupe)</MenuItem>
                      </Select>
                    </FormControl>
                    <MDButton color="info" onClick={ajouter} disabled={!form.libelle.trim()}><Icon>add</Icon></MDButton>
                  </MDBox>
                  <Divider sx={{ mb: 1 }} />
                  {couts.length === 0 && (<MDTypography variant="caption" color="text">Aucun frais saisi.</MDTypography>)}
                  {CATEGORIES.map((cat) => {
                    const liste = couts.filter((c) => (c.categorie || "divers") === cat.val);
                    if (liste.length === 0) return null;
                    const place = bilan ? bilan.places_vendues : 0;
                    const tot = (c) => c.type === "par_personne" ? (Number(c.montant)||0)*place : (Number(c.montant)||0);
                    const sous = liste.reduce((s,c) => s+tot(c), 0);
                    return (
                      <MDBox key={cat.val} mt={1.5}>
                        <MDBox display="flex" justifyContent="space-between" sx={{ bgcolor:"#f8f9fa", borderRadius:"6px", px:1, py:0.5 }}>
                          <MDTypography variant="button" fontWeight="bold">{cat.label}</MDTypography>
                          <MDTypography variant="button" fontWeight="bold">{fmt(sous)} €</MDTypography>
                        </MDBox>
                        {liste.map((c) => (
                          <MDBox key={c.id} display="flex" alignItems="center" py={1} pl={2} sx={{ borderBottom:"1px solid #f5f5f5" }}>
                            <MDBox flex={1}>
                              <MDTypography variant="button">{c.libelle}</MDTypography>
                              <MDTypography variant="caption" color="text" display="block">
                                {c.type === "par_personne" ? fmt(c.montant)+" €/pers × "+place : "forfait groupe"}
                              </MDTypography>
                            </MDBox>
                            <MDTypography variant="button" fontWeight="bold" sx={{ mr:1 }}>{fmt(tot(c))} €</MDTypography>
                            <IconButton size="small" color="error" onClick={() => supprimer(c.id)}><Icon fontSize="small">remove_circle</Icon></IconButton>
                          </MDBox>
                        ))}
                      </MDBox>
                    );
                  })}
                  {couts.length > 0 && (
                    <MDBox display="flex" justifyContent="space-between" pt={2} mt={1} sx={{ borderTop:"2px solid #e0e0e0" }}>
                      <MDTypography variant="h6">Total des dépenses</MDTypography>
                      <MDTypography variant="h6">{fmt(bilan.total_depenses)} €</MDTypography>
                    </MDBox>
                  )}
                </Card>
              </Grid>
            </>
          )}
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default OumrahBilan;
